import {
  decodeActionCard,
  type DeferredDismissState,
  type DismissalQuestion,
  type DismissStrip,
} from '@littlebird/action-cards';
import { getSDK } from '@littlebird/sdk';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { DismissHoldBoundary } from './DismissHoldBoundary';
import { outcomeLabel } from './dismissLabel';

// `list`: the card left the list, so the toast offers Undo unless the dismissal
// is final. `chat`: the card stays folded with its own Restore
export type DismissToastMode = 'list' | 'chat';

const cardTitle = (cardId: string) => {
  const card = getSDK().stores.actionCards.get(cardId);
  const decoded = card ? decodeActionCard(card) : null;
  return decoded ? decoded.summary || decoded.actionLabel : undefined;
};

const FeedbackMenu = ({
  question,
  stripId,
  controller,
  defaultOpen,
}: {
  readonly question: DismissalQuestion;
  readonly stripId: string;
  readonly controller: DeferredDismissState;
  readonly defaultOpen: boolean;
}) => (
  <DropdownMenu.Root modal={false} defaultOpen={defaultOpen}>
    <DropdownMenu.Trigger asChild>
      <Button variant="ghost" size="dense">
        {question.triggerLabel}
        <Icons.chevronDown aria-hidden="true" />
      </Button>
    </DropdownMenu.Trigger>
    {/* Inline, so pointer and focus on the menu still hold the toast */}
    <DropdownMenu.ContentInline align="end">
      {question.options.map((option) => (
        <DropdownMenu.Item
          key={option.key}
          onSelect={() => controller.chooseReason(stripId, option.key)}
        >
          {option.label}
        </DropdownMenu.Item>
      ))}
      {question.allowOther ? (
        <>
          <DropdownMenu.Separator />
          <DropdownMenu.Item onSelect={() => controller.openOther(stripId)}>
            {question.otherLabel}
          </DropdownMenu.Item>
        </>
      ) : null}
    </DropdownMenu.ContentInline>
  </DropdownMenu.Root>
);

const OtherNote = ({
  question,
  onSubmit,
  onBack,
}: {
  readonly question: DismissalQuestion;
  readonly onSubmit: (note: string) => void;
  readonly onBack: () => void;
}) => {
  const [note, setNote] = useState('');
  return (
    <input
      value={note}
      onChange={(event) => setNote(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onSubmit(note);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onBack();
        }
      }}
      maxLength={500}
      placeholder={question.otherPlaceholder}
      autoFocus
      aria-label={question.prompt}
      className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
    />
  );
};

export const DismissToast = ({
  strip,
  controller,
  mode,
}: {
  readonly strip: DismissStrip;
  readonly controller: DeferredDismissState;
  readonly mode: DismissToastMode;
}) => {
  const [reopenMenu, setReopenMenu] = useState(false);
  const title = strip.targets.length === 1 ? cardTitle(strip.id) : undefined;
  const { question } = strip;
  const offersUndo = mode === 'list' && strip.undoable;

  return (
    <DismissHoldBoundary stripIds={[strip.id]} controller={controller}>
      <span
        role="status"
        className="flex w-[var(--width)] flex-col gap-2 rounded-lg border border-border-strong bg-surface px-4 py-3 text-sm text-foreground shadow-lg"
      >
        {title ? <span className="truncate font-medium">{title}</span> : null}
        {strip.stage === 'other' && question ? (
          <OtherNote
            question={question}
            onSubmit={(note) => controller.submitOther(strip.id, note)}
            onBack={() => {
              setReopenMenu(true);
              controller.closeOther(strip.id);
            }}
          />
        ) : (
          <span className="flex items-center gap-2">
            <span
              className={cn(
                'flex-1',
                title ? 'text-foreground-muted' : 'font-medium'
              )}
            >
              {outcomeLabel(strip, mode)}
              {offersUndo ? (
                <span className="sr-only">, undo available</span>
              ) : null}
            </span>
            {strip.stage === 'thanks' ? (
              <span className="text-foreground-muted">
                Thanks for the feedback
              </span>
            ) : question ? (
              <FeedbackMenu
                question={question}
                stripId={strip.id}
                controller={controller}
                defaultOpen={reopenMenu}
              />
            ) : null}
            {offersUndo ? (
              <Button
                variant="outline"
                size="dense"
                onClick={() => controller.undo(strip.id)}
              >
                <Icons.undo aria-hidden="true" />
                Undo
              </Button>
            ) : null}
          </span>
        )}
      </span>
    </DismissHoldBoundary>
  );
};
